import { useState, useEffect } from 'react'
import { User, Calendar, Code, TrendingUp, ChevronRight, ChevronLeft, Copy, Check } from 'lucide-react'
import SessionViewer from './SessionViewer'

/**
 * Participants List Viewer - Browse participants and their sessions
 */
export default function ParticipantsListViewer() {
  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedParticipant, setSelectedParticipant] = useState(null)
  const [participantSessions, setParticipantSessions] = useState(null)
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [selectedSession, setSelectedSession] = useState(null)
  const [sessionDetails, setSessionDetails] = useState(null)
  const [loadingSessionDetails, setLoadingSessionDetails] = useState(false)
  const [copiedId, setCopiedId] = useState(null)
  
  // Load participants list on mount
  useEffect(() => {
    loadParticipants()
  }, [])
  
  const loadParticipants = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/research-data?type=participants-list')
      const data = await response.json()
      setParticipants(data.participants || [])
    } catch (error) {
      console.error('Failed to load participants:', error)
      alert(`Error loading participants: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }
  
  const loadParticipantSessions = async (participantId) => {
    try {
      setLoadingSessions(true)
      const response = await fetch(`/api/research-data?type=participant-sessions&participantId=${participantId}`)
      const data = await response.json()
      setParticipantSessions(data)
      setSelectedParticipant(participantId)
    } catch (error) {
      console.error('Failed to load sessions:', error)
      alert(`Error loading sessions: ${error.message}`)
    } finally {
      setLoadingSessions(false)
    }
  }
  
  const loadSessionDetails = async (sessionId) => {
    try {
      setLoadingSessionDetails(true)
      const response = await fetch(`/api/research-data?type=session-details&sessionId=${sessionId}`)
      const data = await response.json()
      setSessionDetails(data)
      setSelectedSession(sessionId)
    } catch (error) {
      console.error('Failed to load session details:', error)
      alert(`Error loading session details: ${error.message}`)
    } finally {
      setLoadingSessionDetails(false)
    }
  }
  
  const handleBackFromSession = () => {
    setSelectedSession(null)
    setSessionDetails(null)
  }
  
  const handleBackToParticipants = () => {
    setSelectedParticipant(null)
    setParticipantSessions(null)
    setSelectedSession(null)
    setSessionDetails(null)
  }
  
  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000) // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }
  
  // Format dyslexia status for display
  const formatDyslexiaStatus = (status) => {
    const statusMap = {
      'yes': '🧠 Dyslexic',
      'yes_special_school': '🧠 Dyslexic (Special School)',
      'suspected': '🤔 Suspected Dyslexic',
      'no': '✅ Non-Dyslexic',
      'prefer_not': '🔒 Prefer Not to Say',
      'skipped': '⏭️ Skipped',
      'ai_llm': '🤖 AI/LLM Bot'
    }
    return statusMap[status] || status
  }
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    // Database stores UTC timestamps without timezone indicator
    // Append 'Z' to force UTC interpretation before converting to Eastern time
    const utcString = dateString.includes('Z') || dateString.includes('+') 
      ? dateString 
      : dateString.replace(' ', 'T') + 'Z'
    return new Date(utcString).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    })
  }
  
  // View 3: Session details (full iteration viewer)
  if (selectedSession && sessionDetails) {
    return (
      <SessionViewer 
        session={sessionDetails} 
        onBack={handleBackFromSession}
      />
    )
  }
  
  // View 2: Participant's sessions list
  if (selectedParticipant && participantSessions) {
    return (
      <div className="min-h-screen bg-p5-dark text-white p-6">
        {/* Header */}
        <div className="max-w-7xl mx-auto mb-6">
          <button
            onClick={handleBackToParticipants}
            className="mb-4 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors flex items-center gap-2"
          >
            <ChevronLeft size={16} /> Back to Participants
          </button>
          
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">
                  Participant: {participantSessions.participantId.substring(0, 8)}...
                </h2>
                <div className="flex gap-4 text-sm text-gray-400">
                  <span>{formatDyslexiaStatus(participantSessions.dyslexicStatus)}</span>
                  <span>• {participantSessions.codingExperience || 'Unknown experience'}</span>
                  <span>• Joined: {formatDate(participantSessions.participantCreatedAt)}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-p5-pink">
                  {participantSessions.sessions.length}
                </div>
                <div className="text-sm text-gray-400">Sessions</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Sessions Table */}
        <div className="max-w-7xl mx-auto">
          <h3 className="text-xl font-semibold mb-4">All Sessions</h3>
          
          {loadingSessions ? (
            <div className="bg-gray-900 rounded-lg p-8 text-center">
              <div className="text-gray-400">Loading sessions...</div>
            </div>
          ) : participantSessions.sessions.length === 0 ? (
            <div className="bg-gray-900 rounded-lg p-8 text-center">
              <div className="text-gray-400">No sessions found for this participant</div>
            </div>
          ) : (
            <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-800 border-b border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold">Session ID</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold">Started</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold">Ended</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold">Iterations</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold">Success Rate</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {participantSessions.sessions.map((session, idx) => (
                    <tr 
                      key={session.session_id}
                      className={`border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${
                        idx % 2 === 0 ? 'bg-gray-900' : 'bg-gray-900/50'
                      }`}
                    >
                      <td className="px-4 py-3 font-mono text-sm">
                        <div className="flex items-center gap-2">
                          <span>{session.session_id.substring(0, 8)}...</span>
                          <button
                            onClick={() => copyToClipboard(session.session_id, session.session_id)}
                            className="p-1 hover:bg-gray-700 rounded transition-colors"
                            title="Copy full session ID"
                          >
                            {copiedId === session.session_id ? (
                              <Check size={14} className="text-green-400" />
                            ) : (
                              <Copy size={14} className="text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        {formatDate(session.started_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        {session.ended_at ? formatDate(session.ended_at) : (
                          <span className="text-yellow-400">In Progress</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-p5-pink font-semibold">{session.iteration_count}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {session.success_rate !== null ? (
                          <span className={`${
                            session.success_rate >= 0.7 ? 'text-green-400' : 
                            session.success_rate >= 0.4 ? 'text-yellow-400' : 
                            'text-red-400'
                          }`}>
                            {(session.success_rate * 100).toFixed(0)}%
                          </span>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => loadSessionDetails(session.session_id)}
                          disabled={loadingSessionDetails}
                          className="px-3 py-1 bg-p5-pink hover:bg-p5-pink/80 rounded text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          View Details <ChevronRight size={14} className="inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  // View 1: All participants list
  return (
    <div className="min-h-screen bg-p5-dark text-white p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Research Participants</h2>
          <p className="text-gray-400">
            Click on a participant to view all their sessions and interactions
          </p>
        </div>
        
        {loading ? (
          <div className="bg-gray-900 rounded-lg p-8 text-center">
            <div className="text-gray-400">Loading participants...</div>
          </div>
        ) : participants.length === 0 ? (
          <div className="bg-gray-900 rounded-lg p-8 text-center">
            <div className="text-gray-400">No participants found</div>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800 border-b border-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Participant ID</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Dyslexia Status</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Status Updated</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Coding Experience</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold">Sessions</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Last Session</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((participant, idx) => (
                  <tr 
                    key={participant.id}
                    className={`border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${
                      idx % 2 === 0 ? 'bg-gray-900' : 'bg-gray-900/50'
                    }`}
                  >
                    <td className="px-4 py-3 font-mono text-sm">
                      <div className="flex items-center gap-2">
                        <span>{participant.id.substring(0, 8)}...</span>
                        <button
                          onClick={() => copyToClipboard(participant.id, participant.id)}
                          className="p-1 hover:bg-gray-700 rounded transition-colors"
                          title="Copy full participant ID"
                        >
                          {copiedId === participant.id ? (
                            <Check size={14} className="text-green-400" />
                          ) : (
                            <Copy size={14} className="text-gray-400" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {formatDyslexiaStatus(participant.dyslexic_status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {participant.dyslexic_status_updated_at 
                        ? formatDate(participant.dyslexic_status_updated_at) 
                        : formatDate(participant.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {participant.coding_experience || 'Not specified'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${
                        participant.session_count > 0 ? 'text-p5-pink' : 'text-gray-500'
                      }`}>
                        {participant.session_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {participant.last_session_at 
                        ? formatDate(participant.last_session_at) 
                        : formatDate(participant.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => loadParticipantSessions(participant.id)}
                        disabled={participant.session_count === 0 || loadingSessions}
                        className="px-3 py-1 bg-p5-pink hover:bg-p5-pink/80 rounded text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        View Sessions <ChevronRight size={14} className="inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
